import React, { useState, useEffect, useRef } from 'react';
import { Router } from '@abyss/web/ui/Router';
import { Heading } from '@abyss/web/ui/Heading';
import { PlanSection } from '@src/components/PlanSection/PlanSection2';
import { Breadcrumb } from '@src/common/Breadcrumb/Breadcrumb';
import { Benefits } from '@src/common/Benefits/Benefits';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { V2Button } from '@abyss/web/ui/Button';
import { Popover } from '@abyss/web/ui/Popover';
import { PlanNav } from '@src/components/PlanNav';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { PlanNavMobile } from '@src/components/PlanNavMobile/PlanNavMobile';

import data from '@src/data/msplandetails.json';
import styles from './msplandetail.module.css';

import classNames from 'classnames';

export const MSPlanDetail = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isChecked, setChecked] = useState(false);
  const ref = useRef(null);
  const sections = useRef([]);
  const barRef = useRef(null);

  const handleScroll = () => {
    const pageYOffset = window.scrollY;
    let newActiveSection = null;

    sections.current.forEach((section) => {
      const sectionOffsetTop = section.offsetTop - 150;
      const sectionHeight = section.offsetHeight;

      if (
        pageYOffset >= sectionOffsetTop &&
        pageYOffset < sectionOffsetTop + sectionHeight
      ) {
        newActiveSection = section.id;
      }
    });

    setActiveSection(newActiveSection);
  };

  useEffect(() => {
    const header = barRef?.current;
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsSticky(e.isIntersecting);
      },
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] }
    );

    if (header) {
      observer.observe(header);
    }

    // clean up the observer
    return () => {
      observer.unobserve(header);
    };
  }, [barRef]);

  useEffect(() => {
    sections.current = document.querySelectorAll('[data-section]');
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <React.Fragment>
      <Router.MetaTags title="Plan Detail" />

      <section className={styles.head}>
        <div>
          <Breadcrumb
            linkText="View all Medicare Supplement plans (10)"
            location="98198 King County"
          />

          <div className={styles.header}>
            <Heading.Level>
              <Heading>{data.plan.title}</Heading>
            </Heading.Level>

            <div className={styles.description}>{data.plan.description}</div>
          </div>
        </div>
      </section>

      <section>
        <div className={styles.bannerWrap}>
          <div className={styles.banner}>
            <div className={styles.bannerWrap}>
              <div className={styles.discountContainer}>
                <div className={styles.discount}>
                  <div className={styles.discountIcon}>
                    <IconBrand icon="piggy_bank" />
                  </div>
                  <div className={styles.discountContent}>
                    <div className={styles.discountTitle}>
                      Includes a $25 per month discount
                    </div>
                    <div className={styles.discountCopy}>
                      New to Medicare discount applies to each month¹ for the
                      first 12 months.
                    </div>
                    <div className={styles.discountLink}>
                      <V2Button
                        variant="text"
                        icon={{
                          icon: 'beenhere',
                          position: 'leading',
                          variant: 'outlined',
                        }}
                        css={{ padding: '0', minHeight: '0' }}
                      >
                        Check eligibility
                      </V2Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.msMonthly}>
                <div className={styles.msMonthlyTitle}>
                  Estimated Monthly Premium
                </div>
                <div className={styles.msMonthlyPremium}>
                  {data.plan.monthlyPremium}
                </div>
                <div className={styles.msMonthlySub}>
                  Includes {data.plan.discounts} in discounts
                </div>
                <div className={styles.msMonthlyButton}>
                  <V2Button variant="text" className={styles.addButton}>
                    <span className={styles.addWrap}>
                      <span className={styles.addIcon}>
                        <IconSymbol
                          icon="add_box"
                          color="#002677"
                          variant="outlined"
                          size="22px"
                        />
                      </span>
                      <span className={styles.addText}>
                        Add your information to update the estimated premium
                      </span>
                    </span>
                  </V2Button>
                </div>
              </div>

              <ul className={classNames(styles.bannerTop, styles.bannerList)}>
                <li>
                  <div className={styles.bannerTitle}>
                    Part A inpatient hospital deductible{' '}
                  </div>
                  <div className={styles.bannerCopy}>{data.plan.parta}</div>
                </li>
                <li>
                  <div className={styles.bannerTitle}>
                    Annual Part B deductible{' '}
                  </div>
                  <div className={styles.bannerCopy}>
                    {data.plan.partbdeduct}
                  </div>
                </li>
                <li>
                  <div className={styles.bannerTitle}>
                    Out-of-pocket maximum{' '}
                    <Popover
                      title="Concise and clear heading"
                      content="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer vitae molestie risus, ut semper mi."
                    >
                      <IconSymbol
                        icon="info"
                        color="$primary1"
                        size="22px"
                        variant="outlined"
                      />
                    </Popover>
                  </div>
                  <div className={styles.bannerCopy}>{data.plan.oopmax}</div>
                </li>
                <li>
                  <div className={styles.bannerTitle}>
                    Part B excess charges{' '}
                    <Popover
                      title="Concise and clear heading"
                      content="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer vitae molestie risus, ut semper mi."
                    >
                      <IconSymbol
                        icon="info"
                        color="$primary1"
                        size="22px"
                        variant="outlined"
                      />
                    </Popover>
                  </div>
                  <div className={styles.bannerCopy}>{data.plan.partb}</div>
                </li>
              </ul>
            </div>
            <div className={styles.bannerEnd}>
              <div>
                <V2Button variant="filled" className={styles.orange}>
                  Start application
                </V2Button>
              </div>
              <div>
                <V2Button variant="outline">
                  <IconSymbol
                    icon="favorite"
                    color="#002677"
                    variant="outlined"
                    size="24px"
                  />
                  Save
                </V2Button>
              </div>
              <div>
                <Checkbox
                  label="Add to compare"
                  isChecked={isChecked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
              </div>
            </div>
          </div>
        </div>
        <p>
          AARP® Medicare Supplement Insurance Plan insured by UnitedHealthcare
          Insurance Company
        </p>
      </section>

      <section>
        <Benefits
          title="Discover why Plan G + wellness extras may be a good fit"
          items={data.plan.benefits}
        />
      </section>

      <section className={styles.wrapNoPad}>
        <div className={styles.barRef}>
          <div className={isSticky ? styles.stickyFade : ''} />
        </div>
        <div className={styles.planListing}>
          <div className={styles.mobileBox}>
            <div className={styles.planListingTitle}>
              <Heading>Explore the plan details</Heading>
            </div>
          </div>
          <div className={styles.mobileNav}>
            <PlanNavMobile
              items={data.plan.sections}
              activeScroll={activeSection}
            />
          </div>
          <div className={styles.planSide} ref={barRef}>
            <div className={styles.desktopBox}>
              <div className={styles.planListingTitle}>
                <Heading>Explore the plan details</Heading>
              </div>
            </div>
            <div className={styles.desktopNav}>
              <PlanNav
                items={data.plan.sections}
                activeScroll={activeSection}
              />
            </div>
          </div>
          <div className={styles.planRight}>
            {data.plan.sections.map((section, index) => {
              return (
                <PlanSection parent="ms" items={section} layout key={index} />
              );
            })}
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};
